/* eslint-disable max-len */
import React from 'react';
import {
    InputField,
    CheckboxField,
    DropdownSelectField,
    TextAreaField,
    Button
} from '@jutro/components';
import AdacDatePicker from '../AdacDatePicker/AdacDatePicker';
import styles from './ElementMapping.module.scss';
import { parseJSONStringToObject } from './ElementMappingUtil';
import { _ } from 'core-js';

const renderNode = (
    element,
    formState,
    handleChange,
    parentPath,
    validationErrors = {},
    externalDateTouched = false
) => {
    if (!element || !element.tagName) {
        // If element or tagName is not defined, return null to avoid errors
        return null;
    }

    // Generates correct paths for fields being part of object/array. 
    const formStatePath = parentPath !== undefined
        ? `${parentPath}.${element.getAttribute('name')}`
        : element.getAttribute('name');

    if (element.tagName === 'TABLE' && formStatePath !== null && formStatePath !== undefined && _.get(formState, formStatePath) === undefined) {
        // Initialize tables with empty array
        handleChange(formStatePath, []);
    }

    if (element.tagName === 'DIV' && formStatePath !== null && formStatePath !== undefined && _.get(formState, formStatePath) === undefined) {
        // Initialize tables with empty array
        handleChange(formStatePath, {});
    }

    // Populate formState with initial value if the element has a value
    if (element.value && !(formStatePath in formState)) {
        // Checkbox requires special handling
        if (element.type === 'checkbox' && element.checked === true) {
            handleChange(formStatePath, true);
        } else if (element.type === 'checkbox' && element.value === 'on') {
            handleChange(formStatePath, false);
        } else {
            handleChange(formStatePath, element.value);
        }
    }

    // Static table is a non interactive table without data coming from source system. Fully defined in template.
    const renderStaticTable = () => {
        const columns = [...element.querySelectorAll('thead th')];
        const rows = [...element.querySelectorAll('tbody tr')];

        // Fallback for when headers are not present, calculate number of columns dynamically
        const columnCount = columns.length || (rows[0] ? rows[0].children.length : 0);
        const getColumnWidth = (cells) => {
            return cells.map((cell) => {
                if (cell.querySelector('input[type="checkbox"]')) {
                    return 'auto'; // Checkboxes should take minimal space
                }
                return `${100 / columnCount}%`; // Default to equal width if content is text
            });
        };

        return (
            <div className={styles.tableWrapper}>
                {/* Header */}
                {columns.length > 0 && (
                    <div className={styles.tableHeader}>
                        {columns.map((col, colIndex) => (
                            <div
                                key={`col-${colIndex}-${col.getAttribute('uniqueId')}`}
                                className={styles.tableHeaderCell}
                                style={{ width: `${100 / columnCount}%` }}
                            >
                                {col.textContent}
                            </div>
                        ))}
                    </div>
                )}

                {/* Body */}
                <div className={styles.tableBody}>
                    {rows.map((row, rowIndex) => {
                        const cells = [...row.querySelectorAll('td')];

                        // Get dynamic column widths based on content
                        const columnWidths = getColumnWidth(cells);

                        return (
                            <div key={`row-${rowIndex}-${element.getAttribute('uniqueId')}`} className={styles.tableRow}>
                                {cells.map((cell, cellIndex) => {
                                    const firstChildElement = [...cell.childNodes].find((node) => node.nodeType === 1);

                                    return (
                                        <div
                                            key={`cell-${rowIndex}-${cellIndex}-${cell.getAttribute('uniqueId')}`}
                                            className={styles.tableCell}
                                            style={{ width: columnWidths[cellIndex] }}
                                        >
                                            {firstChildElement && renderNode(firstChildElement, formState, handleChange, undefined, validationErrors, externalDateTouched)}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Utility function for dynamic table
    const createNewTableEntry = () => {
        const existingData = _.get(formState, formStatePath);
        const maxObjectId = existingData.reduce((max, obj) => {
            return obj.entryId > max ? obj.entryId : max;
        }, 0);

        existingData.push({
            entryId: maxObjectId + 1
        });

        handleChange(formStatePath, existingData);
    };

    // Utility function for dynamic table
    const removeEntry = (entryToRemove) => {
        const existingData = _.get(formState, formStatePath);
        const newData = existingData.filter((entry) => entry.entryId !== entryToRemove.entryId);

        handleChange(formStatePath, newData);
    };

    // Dynamic table is a interactive table that allows to create new complex object as defined in template definition.
    // Important note: IT DOES NOT SUPPORT SHOWING EXISTING SOURCE SYSTEM DATA.
    const renderDynamicTable = () => {
        const columns = [...element.querySelectorAll('thead th')];

        // Fallback for when headers are not present, calculate number of columns dynamically
        const columnCount = columns.length;
        const actionColumnSize = 15;
        const singleColumnSize = `${(100 - actionColumnSize) / columnCount}%`; // 80 because Actions should take 20 regardless

        let errors = [];
        if (formStatePath !== undefined && formStatePath !== null) {
            errors = _.get(validationErrors, `${formStatePath}.validationMessages`);
        }

        return (
            <div className={_.isEmpty(errors) ? styles.tableWrapper : styles.tableWrapperInError}>
                <Button className={styles.newEntryButton} onClick={createNewTableEntry}>
                    Neuer Eintrag
                </Button>
                {/* Header */}
                {columns.length > 0 && (
                    <div className={styles.tableHeader}>
                        {columns.map((col, colIndex) => (
                            <div
                                key={`col-${colIndex}-${col.getAttribute('uniqueId')}`}
                                className={styles.tableHeaderCell}
                                style={{ width: singleColumnSize }}
                            >
                                {col.textContent}
                            </div>
                        ))}
                        <div
                            key={`${formStatePath}-col-actions`}
                            className={styles.tableHeaderCell}
                            style={{ width: `${actionColumnSize}%` }}
                        >
                            Aktionen
                        </div>
                    </div>
                )}

                {/* Body */}
                <div className={styles.tableBody}>
                    {(!_.isEmpty(formState) && !_.isEmpty(_.get(formState, formStatePath))) && _.get(formState, formStatePath).map((entry, index) => {
                        const row = [...element.querySelectorAll('tbody tr')][0];//  There should be only one row
                        const cells = [...row.querySelectorAll('td')];

                        return (
                            <div key={`row-${index}-${element.getAttribute('uniqueId')}`} className={styles.tableRow}>
                                {cells.map((cell, cellIndex) => {
                                    const firstChildElement = [...cell.childNodes].find((node) => node.nodeType === 1);

                                    return (
                                        <div
                                            key={`cell-${index}-${cellIndex}-${cell.getAttribute('uniqueId')}`}
                                            className={styles.tableCell}
                                            style={{ width: singleColumnSize }}
                                        >
                                            {firstChildElement && renderNode(firstChildElement, formState, handleChange, `${formStatePath}[${index}]`, validationErrors, externalDateTouched)}
                                        </div>
                                    );
                                })}
                                <div
                                    key={`${formStatePath}-col-actions`}
                                    className={styles.tableCell}
                                    style={{ width: `${actionColumnSize}%` }}
                                >
                                    <Button className={styles.actionButton} icon="mi-delete" onClick={() => removeEntry(entry)} type="text">
                                        Entfernen
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {!_.isEmpty(errors) && (
                    <div>
                        {errors.map((error, index) => {
                            const elementId = `${formStatePath}_error${index}`;
                            return (
                                <span id={elementId} key={elementId} className={styles.spanRedElement}>
                                    {error}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const validationMessages = _.get(validationErrors, `${formStatePath?.replace('[', '.children[')}.validationMessages`) || [];
    const showError = !_.isEmpty(validationMessages);

    switch (element.tagName.toLowerCase()) {
        case 'input':
            switch (element.type) {
                case 'text':
                case 'email':
                    return (
                        <InputField
                            key={element.getAttribute('uniqueId')}
                            id={element.id}
                            name={element.name}
                            label={element.getAttribute('label')}
                            required={element.required}
                            value={_.get(formState, formStatePath)}
                            maxLength={Number(element.getAttribute('maxLength')) || undefined}
                            disabled={element.readOnly}
                            onValueChange={(value) => handleChange(formStatePath, value)}
                            validationMessages={validationMessages}
                            showErrors={showError}
                            className={styles.input}
                        />
                    );
                case 'date': {
                    const validationRules = parseJSONStringToObject(element.getAttribute('validateDate') || '{}');

                    return (
                        <AdacDatePicker
                            key={element.getAttribute('uniqueId')}
                            date={_.get(formState, formStatePath) || null}
                            setDate={(date) => handleChange(formStatePath, date)}
                            label={element.getAttribute('label')}
                            placeholder={element.placeholder}
                            isValid={!showError}
                            required={element.required}
                            validationMessage={showError ? validationMessages[0] : ''}
                            minYear={validationRules.minDate}
                            maxYear={validationRules.maxDate}
                            externalTouched={externalDateTouched}
                        />
                    );
                }
                case 'checkbox':
                    return (
                        <CheckboxField
                            key={element.getAttribute('uniqueId')}
                            id={element.id}
                            name={element.name}
                            className={styles.checkbox}
                            label={element.getAttribute('label')}
                            required={element.required}
                            checked={element.checked || false}
                            value={_.get(formState, formStatePath) || false}
                            onValueChange={(value) => handleChange(formStatePath, value)}
                            disabled={element.readOnly}
                            showInlineLabel
                            validationMessages={validationMessages}
                            showErrors={showError}
                        />
                    );
                default:
                    return null;
            }
        case 'textarea':
            return (
                <TextAreaField
                    key={element.getAttribute('uniqueId')}
                    id={element.id}
                    name={element.name}
                    className={styles.input}
                    label={element.getAttribute('label')}
                    rows={element.rows}
                    cols={element.cols}
                    maxLength={Number(element.getAttribute('maxLength')) || undefined}
                    value={_.get(formState, formStatePath) || ''}
                    onValueChange={(value) => handleChange(formStatePath, value)}
                    validationMessages={validationMessages}
                    showErrors={showError}
                    disabled={element.readOnly}
                />
            );
        case 'select':
            return (
                <DropdownSelectField
                    key={element.getAttribute('uniqueId')}
                    id={element.id}
                    name={element.name}
                    className={styles.input}
                    label={element.getAttribute('label')}
                    availableValues={[...element.children].map((option) => ({
                        code: option.value,
                        name: option.textContent
                    }))}
                    value={_.get(formState, formStatePath) || ''}
                    onValueChange={(value) => handleChange(formStatePath, value)}
                    required={element.required}
                    validationMessages={validationMessages}
                    showErrors={showError}
                />
            );
        case 'table': {
            return element.getAttribute('name') === undefined || element.getAttribute('name') === null
                ? renderStaticTable()
                : renderDynamicTable();
        }
        case 'div': {
            let errors = [];
            const inlineElements = [...element.children]; // Get all child elements for inline layout
            const elementName = element.getAttribute('name');
            if (elementName !== undefined && elementName !== null) {
                errors = _.get(validationErrors, `${elementName}.validationMessages`);
            }

            return (
                <div className={_.isEmpty(errors) ? styles.divElementContainer : styles.divElementErrorContainer}>
                    <div className={styles.inlineWrapper}>
                        {inlineElements.map((childElement) => {
                            return (
                                <div
                                    key={childElement.getAttribute('uniqueId')}
                                    className={styles.inlineElement}
                                >
                                    {!_.isEmpty(element.getAttribute('name')) && renderNode(childElement, formState, handleChange, formStatePath, validationErrors, externalDateTouched)}
                                    {_.isEmpty(element.getAttribute('name')) && renderNode(childElement, formState, handleChange, undefined, validationErrors, externalDateTouched)}
                                </div>
                            );
                        })}
                    </div>
                    <div>
                        {!_.isEmpty(errors) && (
                            <div>
                                {errors.map((error, index) => {
                                    const elementId = `${elementName}_error${index}`;
                                    return (
                                        <span id={elementId} key={elementId} className={styles.spanRedElement}>
                                            {error}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        case 'ul': {
            const listItems = [...element.children];

            return (
                <ul className={styles.ulWrapper}>
                    {listItems.map((childElement,) => {
                        return (
                            <li key={childElement.getAttribute('uniqueId')} className={styles.liElement}>
                                {renderNode(childElement, formState, handleChange, undefined, validationErrors, externalDateTouched)}
                            </li>
                        );
                    })}
                </ul>
            );
        }

        case 'li': {
            const liChildren = [...element.children];
            const hasCheckbox = liChildren.some((child) => child.tagName.toLowerCase() === 'input' && child.type === 'checkbox');
            const className = hasCheckbox ? 'checkbox' : 'input';
            return (
                <div className={`${styles.liWrapper} ${styles[className]}`}>
                    {liChildren.map((childElement) => {
                        return renderNode(childElement, formState, handleChange, undefined, validationErrors, externalDateTouched);
                    })}
                </div>
            );
        }
        case 'button':
            return (
                <Button
                    key={element.getAttribute('uniqueId')}
                    id={element.id}
                    actionType={element.type}
                >
                    {element.textContent}
                </Button>
            );
        case 'label':
            return null;
        case 'br':
            return <br key={element.getAttribute('uniqueId')} />;
        case 'h1':
            return <h1 key={element.getAttribute('uniqueId')}>{element.textContent}</h1>;
        case 'h2':
            return <h2 key={element.getAttribute('uniqueId')}>{element.textContent}</h2>;
        case 'h3':
            return <h3 key={element.getAttribute('uniqueId')}>{element.textContent}</h3>;
        case 'h4':
            return <h4 key={element.getAttribute('uniqueId')}>{element.textContent}</h4>;
        case 'h5':
            return <h5 key={element.getAttribute('uniqueId')}>{element.textContent}</h5>;
        case 'h6':
            return <h6 key={element.getAttribute('uniqueId')}>{element.textContent}</h6>;
        case 'p':
            return <p key={element.getAttribute('uniqueId')}>{element.textContent}</p>;
        case 'span':
            return <span key={element.getAttribute('uniqueId')}>{element.textContent}</span>;
        default:
            return null;
    }
};

export default renderNode;
